/**
 * PaymentApprovalModal — owner-of-tenant SMS approval flow.
 *
 * Wraps the 2-step /payments/sms-challenge + /payments/sms-verify pattern
 * used by the credit-memo + pre-credit endpoints in `credit_memos_v2.py`.
 * Returns a short-lived `approval_token` via `onApproved(token)` callback.
 * Caller passes that token in the request body of the privileged endpoint.
 *
 * Usage:
 *   <PaymentApprovalModal
 *      open={open} onClose={…}
 *      action="issue_credit_memo"        // or "deposit_pre_credit"
 *      invoiceId={inv.id}                // optional
 *      amount={amount}                   // display-only
 *      title={`Confirm $${amt} credit memo`}
 *      description="We'll text a 6-digit code to your account owner."
 *      onApproved={(token) => callBackend(token)}
 *   />
 */
import { useEffect, useRef, useState } from "react";
import { ShieldCheck, Loader2, KeyRound, Info } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { toast } from "sonner";
import api, { formatErr } from "../lib/api";

export default function PaymentApprovalModal({
  open, onClose, action, invoiceId, amount, title, description, onApproved,
}) {
  const [phase, setPhase] = useState("send");      // send | enter | verifying
  const [code, setCode]   = useState("");
  const [ctx, setCtx]     = useState(null);        // {phone_masked, owner_name, sent_to_owner, dev_mode}
  const [err, setErr]     = useState("");
  const sentRef = useRef(false);

  useEffect(() => {
    if (!open) {
      sentRef.current = false;
      setPhase("send"); setCode(""); setErr(""); setCtx(null);
      return;
    }
    if (sentRef.current) return;
    sentRef.current = true;
    (async () => {
      setErr("");
      try {
        const { data } = await api.post("/payments/sms-challenge", {
          invoice_id: invoiceId || null, action, amount: amount || null,
        });
        setCtx({
          challenge_id:   data.challenge_id,
          phone_masked:   data.phone_masked,
          owner_name:     data.owner_name,
          sent_to_owner:  data.sent_to_owner,
          dev_mode:       data.dev_mode,
        });
        setPhase("enter");
      } catch (e) {
        const detail = e?.response?.data?.detail;
        if (detail?.code === "owner_phone_not_verified") {
          setErr(detail.message || "Account owner phone isn't verified yet.");
        } else {
          setErr(formatErr(detail) || e.message || "Could not send code");
        }
        setPhase("send");
      }
    })();
  }, [open, action, invoiceId, amount]);

  const submit = async (e) => {
    e?.preventDefault();
    if (code.length < 4 || !ctx) return;
    setPhase("verifying"); setErr("");
    try {
      const { data } = await api.post("/payments/sms-verify", {
        challenge_id: ctx.challenge_id, code: code.trim(),
      });
      toast.success("Approved");
      onApproved?.(data.approval_token);
      onClose?.();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      setErr(detail?.message || (typeof detail === "string" ? detail : "That code didn't work"));
      setPhase("enter");
    }
  };

  const resend = async () => {
    setErr("");
    try {
      const { data } = await api.post("/payments/sms-challenge", {
        invoice_id: invoiceId || null, action, amount: amount || null,
      });
      setCtx({
        challenge_id:   data.challenge_id,
        phone_masked:   data.phone_masked,
        owner_name:     data.owner_name,
        sent_to_owner:  data.sent_to_owner,
        dev_mode:       data.dev_mode,
      });
      setCode("");
      toast.success("New code sent");
    } catch (e) {
      setErr(formatErr(e.response?.data?.detail) || "Could not resend");
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent className="max-w-sm" data-testid="payment-approval-modal">
        <DialogHeader>
          <div className="w-10 h-10 rounded-full bg-foreground/5 grid place-items-center mb-2">
            <ShieldCheck className="w-5 h-5"/>
          </div>
          <DialogTitle data-testid="approval-title">{title || "Confirm with SMS code"}</DialogTitle>
          <DialogDescription>
            {description || "Your account owner will receive a 6-digit code."}
          </DialogDescription>
        </DialogHeader>

        {phase === "send" && (
          <div className="py-6 text-center">
            <Loader2 className="w-5 h-5 animate-spin mx-auto text-foreground/60"/>
            <p className="text-sm text-foreground/60 mt-3">Sending code…</p>
            {err && <p className="text-sm text-destructive mt-3" data-testid="approval-error">{err}</p>}
          </div>
        )}

        {(phase === "enter" || phase === "verifying") && ctx && (
          <form onSubmit={submit} className="space-y-4">
            <div className="text-[13px] text-foreground/70 leading-relaxed">
              We sent a 6-digit code to <span className="font-medium text-foreground">{ctx.phone_masked}</span>
              {ctx.sent_to_owner && ctx.owner_name && (
                <> · the account owner <span className="font-medium">{ctx.owner_name}</span></>
              )}.
              Enter the code to authorize this transaction.
            </div>
            {ctx.dev_mode && (
              <div className="rounded-lg bg-amber-50 border border-amber-200 px-3 py-2 text-[11.5px] text-amber-900 flex items-start gap-1.5">
                <Info className="w-3 h-3 mt-0.5 shrink-0"/>
                Dev/preview mode — Twilio isn't configured, so any 4+ digit code will work.
              </div>
            )}
            <div className="relative">
              <KeyRound className="w-4 h-4 text-foreground/40 absolute left-3 top-1/2 -translate-y-1/2"/>
              <Input
                autoFocus
                inputMode="numeric"
                autoComplete="one-time-code"
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                placeholder="123 456"
                maxLength={6}
                className="pl-9 h-12 text-center text-lg tracking-[0.4em]"
                data-testid="approval-code"
                disabled={phase === "verifying"}
              />
            </div>
            {err && <div className="text-sm text-destructive" data-testid="approval-error">{err}</div>}
            <Button
              type="submit"
              disabled={code.length < 4 || phase === "verifying"}
              data-testid="approval-submit"
              className="w-full h-11 rounded-full bg-foreground text-background hover:bg-foreground/90"
            >
              {phase === "verifying" ? "Verifying…" : "Confirm"}
            </Button>
            <button
              type="button"
              onClick={resend}
              data-testid="approval-resend"
              className="w-full text-[12px] text-foreground/55 hover:text-foreground"
            >
              Didn't get it? Send a new code
            </button>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
